import { useGetPicrPositionDetailsQuery } from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ProfileNameFields } from "~/pages/Compliance/utils/types";
import { getInvestingProfileName, getProfileName } from "../../Profiles/utils";

type EscalationType = NonNullable<
  NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"]
>[0];

type PicrPositionData = ReturnType<typeof useGetPicrPositionDetailsQuery>["data"];

export const getProfileNameForEscalation = (
  profilesForCase: GetComplianceCaseQuery["complianceCase"] | null | undefined,
  escalation: EscalationType | undefined,
  isPicrCase: boolean,
  picrPositionData: PicrPositionData,
): string => {
  const nameById = new Map<number, string>();

  // Base profiles in this case
  for (const p of profilesForCase?.profiles || []) {
    if (!p) continue;
    nameById.set(p.id, getProfileName(p));
  }

  const investingProfile = profilesForCase?.investingProfile;
  if (investingProfile?.id) {
    nameById.set(investingProfile.id, getInvestingProfileName(investingProfile));
  }

  // PICR created/linked profiles
  if (isPicrCase && picrPositionData?.picrPositionDetailsByCaseId) {
    for (const pos of picrPositionData.picrPositionDetailsByCaseId) {
      // Handle created profiles
      const created = (pos as { createdProfile?: { id?: number } | null }).createdProfile as
        Parameters<typeof getProfileName>[0] | undefined;
      if (created?.id) {
        nameById.set(created.id, getProfileName(created));
      }

      // Handle linked profiles (direct linkedProfile)
      const linkedProfile = (pos as { linkedProfile?: { id?: number } | null })
        .linkedProfile as Parameters<typeof getProfileName>[0] | undefined;
      if (linkedProfile?.id) {
        nameById.set(linkedProfile.id, getProfileName(linkedProfile));
      }

      const linkedCase = (
        pos as {
          linkedOnboardingCase?: { profiles?: Array<{ id?: number } | null> | null } | null;
        }
      ).linkedOnboardingCase;
      if (linkedCase?.profiles) {
        for (const linkedCaseProfile of linkedCase.profiles) {
          if (linkedCaseProfile?.id) {
            nameById.set(
              linkedCaseProfile.id,
              getProfileName(linkedCaseProfile as Parameters<typeof getProfileName>[0]),
            );
          }
        }
      }

      // Handle linked investing profiles (from linkedOnboardingCase)
      const linkedInvesting = (
        pos as {
          linkedOnboardingCase?: { investingProfile?: ProfileNameFields | null } | null;
        }
      ).linkedOnboardingCase?.investingProfile;
      if (linkedInvesting?.id) {
        nameById.set(linkedInvesting.id, getInvestingProfileName(linkedInvesting));
      }
    }
  }

  // Look up the specific profile name
  const profileId = (escalation as unknown as { profileId?: number | null })?.profileId;
  if (typeof profileId === "number") {
    const found = nameById.get(profileId);
    if (found) return found;
  }

  // Fallback to investing profile
  if (investingProfile?.id) {
    const fallback = nameById.get(investingProfile.id);
    if (fallback) return fallback;
  }

  return "";
};
